/**
 * Le fil vers lequel l'écran s'apprête à partir, entre l'annonce et le départ (#1588).
 *
 * Tant que la bascule du dock était SYNCHRONE du signal d'écriture, une seule
 * lecture suffisait à savoir que l'écran rejoignait ce fil : `window.location`
 * désignait déjà `/assistant?c=<fil>` quand l'exécuteur reprenait la main.
 * C'est sur cette lecture que `WebSocketPool.laisserLEcranRejoindreLeFil`
 * s'autorisait à attendre l'emplacement avant un `write_markdown mode:"replace"`,
 * seul verbe qui juge ce que l'avocat A SOUS LES YEUX (#1586).
 *
 * La transition douce a glissé `DELAI_DANNONCE_AVANT_LA_BASCULE_MS` entre les
 * deux. Pendant cette fenêtre l'onglet montre encore l'écran quitté : l'attente
 * ne s'armait plus, l'exécuteur lisait « document parqué » et refusait en
 * `REPLACE_REFUSED_CANVAS_NOT_VISIBLE` — l'agent expliquait alors à l'avocat
 * qu'il ne voyait pas le document qui allait justement s'ouvrir sous ses yeux
 * (reproduit par `tests/ws-mocked/canvas-bascule-dock-vers-assistant.spec.ts`,
 * 2026-09-18).
 *
 * Ce registre rend à l'invariant sa forme complète : « l'écran est EN TRAIN de
 * rejoindre ce fil » = la route l'affiche déjà, OU une bascule est annoncée
 * pour lui. Un seul fil à la fois — le hook n'arme qu'une bascule par montage —
 * et rien n'est persisté : l'annonce meurt avec elle, qu'elle aboutisse ou
 * qu'elle soit abandonnée.
 *
 * Volontairement hors de Zustand, contrairement à `retourDepuisLaBascule` et à
 * `brouillonComposeurRelais` : aucun composant ne s'y abonne, le seul lecteur
 * est un service qui interroge impérativement. Un store ne servirait qu'à
 * ajouter une indirection.
 */

/** Le fil annoncé, ou `null` hors de toute fenêtre d'annonce. */
let filAnnonce: string | null = null;

/**
 * Le hook vient de poser l'annonce : à partir d'ici, l'écran rejoint ce fil.
 *
 * Appelé DANS l'émission du signal d'écriture, donc avant que le même bloc
 * synchrone n'atteigne `laisserLEcranRejoindreLeFil` — c'est ce qui garantit
 * que l'attente voit l'annonce.
 */
export function annoncerLaBasculeDuDock(conversationId: string): void {
  filAnnonce = conversationId;
}

/**
 * L'annonce a fait son office ou a été retirée (départ, abandon, démontage du
 * dock, changement de fil).
 *
 * Ciblé par fil : un retrait tardif ne doit pas effacer l'annonce d'une bascule
 * plus récente.
 */
export function oublierLAnnonceDeBasculeDuDock(conversationId: string): void {
  if (filAnnonce === conversationId) filAnnonce = null;
}

/** Une bascule est-elle annoncée vers ce fil, et pas encore partie ? */
export function uneBasculeDuDockEstAnnoncee(conversationId: string): boolean {
  return filAnnonce === conversationId;
}

/** @internal Isolation entre deux tests ou deux stories. */
export function oublierLesAnnoncesDeBasculeDuDock(): void {
  filAnnonce = null;
}
